import { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material'
import { toast } from 'react-toastify'
import { selectEmrSectionsData } from 'src/clinic/reducers/prescription/prescriptionReducer'
import { getMPDataWithParams } from 'src/mentor/services'
import { getSPDataWithParams } from 'src/supportPortal/services/services'
import ViewOnlyPrescriptionV2 from 'src/mentor/components/PrescriptionMakingPage/viewOnlyComponents/ViewOnlyPrescriptionV2'
import { savePrescriptionTemplateApiCall as saveMentorTemplate } from './hooks/usePrescriptionTemplateActions'
import { savePrescriptionTemplateApiCall as saveSupportTemplate } from 'src/supportPortal/views/doctors/prescriptionTemplates/hooks/useSupportPrescriptionTemplateApiActions'

/**
 * Walks the practitioner through every prescription pad (template) that would NOT already follow a
 * just-changed practitioner-wide preferred language — i.e. pads with an explicit, divergent
 * `preferredLanguage` — and lets them apply the new language pad-by-pad with a before/after preview.
 * Works in both the mentor portal (own pads) and the support portal (a doctor's pads).
 */
function languageDisplay(code, appLanguages) {
  if (!code) return 'Default'
  const found = (appLanguages || []).find((l) => l?.id === code)
  if (found?.language) return found.language
  if (code === 'hi') return 'Hindi'
  if (code === 'en') return 'English'
  return code
}

function padLabel(pad) {
  return (
    pad?.headline ||
    pad?.doctorName ||
    (pad?.prescriptionTemplateType ? `${pad.prescriptionTemplateType} pad` : '') ||
    'Prescription pad'
  )
}

// One scaled-down A4 preview (before/after). The language only flips the Rx-table labels +
// patient explainer, both computed live from prescriptionData.preferredLanguage.
function PadPreview({ label, previewData, prescriptionViewSettings, doctorEmrSections, practitionerId }) {
  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
        {label}
      </Typography>
      <Box
        sx={{
          border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden',
          height: 360, background: '#fff', mt: 0.5,
        }}
      >
        {previewData ? (
          <Box sx={{ transform: 'scale(0.44)', transformOrigin: 'top left', width: 794, pointerEvents: 'none' }}>
            <ViewOnlyPrescriptionV2
              prescriptionData={previewData}
              hidePrintMarginsControl
              prescriptionViewSettings={prescriptionViewSettings}
              doctorEmrSections={doctorEmrSections}
              practitionerId={practitionerId}
            />
          </Box>
        ) : (
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="caption" color="text.secondary">Preview unavailable</Typography>
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default function ApplyLanguageToPadsWizard({
  open,
  onClose,
  targetLanguage,
  appLanguages,
  prescriptionViewSettings,
  isSupport = false,
  practitionerId,
  practitionerSlug,
}) {
  const emrData = useSelector(selectEmrSectionsData)
  const doctorEmrSections = (emrData?.data || {})?.prescription?.emrSections || []

  const [pads, setPads] = useState(null) // null = loading
  const [stepIndex, setStepIndex] = useState(0)
  const [dummy, setDummy] = useState(null)
  const [loadingDummy, setLoadingDummy] = useState(false)
  const [savingId, setSavingId] = useState(null)
  const [appliedCount, setAppliedCount] = useState(0)
  const [done, setDone] = useState(false)

  const targetLabel = languageDisplay(targetLanguage, appLanguages)

  // Load the pads that need review when the wizard opens.
  useEffect(() => {
    if (!open) return undefined
    let cancelled = false
    ;(async () => {
      setPads(null)
      setStepIndex(0)
      setDone(false)
      setAppliedCount(0)
      try {
        let resp
        if (isSupport) {
          const params = {}
          if (practitionerSlug) params['practitioner-slug'] = practitionerSlug
          else if (practitionerId) params['practitioner-id'] = practitionerId
          resp = await getSPDataWithParams(
            '/api/secure/cc/support/practitioner/prescription-template/all',
            params,
          )
        } else {
          resp = await getMPDataWithParams('/api/secure/doctor/prescription-template/all', {})
        }
        const raw = resp?.data?.data || resp?.data || []
        const all = Array.isArray(raw) ? raw : []
        // Pads with no explicit language already follow the new practitioner-wide default; only
        // pads whose explicit preferredLanguage diverges from the target need updating.
        const review = all.filter((p) => p?.preferredLanguage && p.preferredLanguage !== targetLanguage)
        if (!cancelled) setPads(review)
      } catch {
        if (!cancelled) {
          setPads([])
          toast.error('Could not load your prescription pads')
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [open, isSupport, practitionerId, practitionerSlug, targetLanguage])

  const currentPad = pads && pads[stepIndex]

  // Fetch the dummy preview data for the current pad (once per pad — language flips client-side).
  useEffect(() => {
    if (!open || !currentPad) {
      setDummy(null)
      return undefined
    }
    let cancelled = false
    ;(async () => {
      setLoadingDummy(true)
      setDummy(null)
      try {
        const params = { clinicId: currentPad.clinicId }
        if (currentPad.id) params.templatePrescriptionId = currentPad.id
        let resp
        if (isSupport) {
          if (practitionerSlug) params['practitioner-slug'] = practitionerSlug
          else if (practitionerId) params['practitioner-id'] = practitionerId
          resp = await getSPDataWithParams(
            '/api/secure/cc/support/practitioner/prescription/dummy',
            params,
          )
        } else {
          resp = await getMPDataWithParams('/api/secure/doctor/prescription/dummy', params)
        }
        const data = resp?.data?.data || {}
        if (!cancelled) setDummy(data?.metaPrescription || null)
      } catch {
        if (!cancelled) setDummy(null)
      } finally {
        if (!cancelled) setLoadingDummy(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [open, currentPad, isSupport, practitionerId, practitionerSlug])

  const buildPreviewData = useCallback(
    (langOverride) => {
      if (!currentPad) return null
      if (!dummy?.prescriptionData) return null
      return {
        ...currentPad,
        preferredLanguage: langOverride,
        prescriptionData: dummy?.prescriptionData,
        patientPrefill: dummy?.patientPrefill,
      }
    },
    [currentPad, dummy],
  )

  const goNext = useCallback(() => {
    setStepIndex((idx) => {
      if (pads && idx < pads.length - 1) return idx + 1
      setDone(true)
      return idx
    })
  }, [pads])

  const applyToPad = useCallback(
    async (pad) => {
      setSavingId(pad.id)
      try {
        const templateToSave = { ...pad, preferredLanguage: targetLanguage }
        if (isSupport) {
          await saveSupportTemplate({
            prescriptionTemplate: templateToSave,
            clinicId: pad.clinicId,
            practitionerId,
            practitionerSlug,
          })
        } else {
          await saveMentorTemplate({ prescriptionTemplate: templateToSave, clinicId: pad.clinicId })
        }
        setAppliedCount((c) => c + 1)
        return true
      } catch {
        toast.error(`Could not update "${padLabel(pad)}"`)
        return false
      } finally {
        setSavingId(null)
      }
    },
    [isSupport, practitionerId, practitionerSlug, targetLanguage],
  )

  const handleApplyAndNext = useCallback(async () => {
    if (!currentPad) return
    const ok = await applyToPad(currentPad)
    if (ok) goNext()
  }, [currentPad, applyToPad, goNext])

  const handleApplyAll = useCallback(async () => {
    if (!pads) return
    const remaining = pads.slice(stepIndex)
    for (const pad of remaining) {
      // sequential so each save settles (and toasts surface) before the next
      // eslint-disable-next-line no-await-in-loop
      await applyToPad(pad)
    }
    setDone(true)
  }, [pads, stepIndex, applyToPad])

  const total = pads?.length || 0
  const savingAll = savingId != null && total > 1

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Apply <strong>{targetLabel}</strong> to your prescription pads?
      </DialogTitle>
      <DialogContent dividers>
        {pads === null && (
          <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        )}

        {pads !== null && total === 0 && (
          <Typography sx={{ py: 4 }} color="text.secondary">
            All your prescription pads already print in <strong>{targetLabel}</strong> (or follow your
            default language). Nothing to update.
          </Typography>
        )}

        {pads !== null && total > 0 && !done && currentPad && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Chip size="small" label={`Pad ${stepIndex + 1} of ${total}`} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {padLabel(currentPad)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {`${languageDisplay(currentPad.preferredLanguage, appLanguages)} → ${targetLabel}`}
              </Typography>
            </Box>
            {loadingDummy ? (
              <Box sx={{ height: 360, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <PadPreview
                  label={`Before — ${languageDisplay(currentPad.preferredLanguage, appLanguages)}`}
                  previewData={buildPreviewData(currentPad.preferredLanguage)}
                  prescriptionViewSettings={prescriptionViewSettings}
                  doctorEmrSections={doctorEmrSections}
                  practitionerId={practitionerId}
                />
                <PadPreview
                  label={`After — ${targetLabel}`}
                  previewData={buildPreviewData(targetLanguage)}
                  prescriptionViewSettings={prescriptionViewSettings}
                  doctorEmrSections={doctorEmrSections}
                  practitionerId={practitionerId}
                />
              </Box>
            )}
          </Box>
        )}

        {done && (
          <Typography sx={{ py: 4 }}>
            {appliedCount > 0
              ? `Updated ${appliedCount} pad${appliedCount === 1 ? '' : 's'} to ${targetLabel}.`
              : 'No pads were changed.'}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        {pads !== null && total > 0 && !done && (
          <>
            <Button onClick={goNext} disabled={savingId != null} color="inherit">
              Skip
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button onClick={handleApplyAll} disabled={savingId != null}>
              {savingAll ? 'Applying…' : 'Apply to all remaining'}
            </Button>
            <Button variant="contained" onClick={handleApplyAndNext} disabled={savingId != null}>
              {savingId === currentPad?.id ? 'Applying…' : 'Apply & Next'}
            </Button>
          </>
        )}
        {(done || (pads !== null && total === 0)) && (
          <Button variant="contained" onClick={onClose}>
            Done
          </Button>
        )}
        {pads === null && (
          <Button onClick={onClose} color="inherit">
            Cancel
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
